import type { GameEvent } from '../../core/engine';
import { sound } from './audio';
import { fmt, ordinal } from './format';
import { game, type EventSource } from './game.svelte';
import { settings } from './settings.svelte';
import { toasts } from './toasts.svelte';

/** Medal counts worth calling out. 168 and 1,229 complete the primes below 1,000 and 10,000. */
const MEDAL_MILESTONES = new Set([
  1, 10, 25, 50, 100, 168, 250, 500, 1_000, 1_229, 2_500, 5_000, 9_592,
]);

function buzz(pattern: number | number[]): void {
  if (settings.haptics && 'vibrate' in navigator) navigator.vibrate(pattern);
}

/**
 * Turns game events into sound, haptics and toasts. Per tap only the most significant
 * sound plays, so fast tapping with Stride never turns into noise.
 */
let notify = true;

function onEvents(events: readonly GameEvent[], source: EventSource): void {
  const rank = { tick: 0, prime: 1, famous: 2, twin: 3, blocked: 4 } as const;
  type Cue = keyof typeof rank;
  // Widened on purpose: TS can't see the closure below assigning to it.
  let top = null as Cue | null;
  const raise = (k: Cue) => {
    if (top === null || rank[k] > rank[top]) top = k;
  };

  for (const e of events) {
    switch (e.type) {
      case 'step':
        raise('tick');
        break;
      case 'prime': {
        raise('prime');
        // Primes are always reached in order, so a prime's index is the medal count.
        const count = e.index;
        if (MEDAL_MILESTONES.has(count)) {
          if (notify)
            toasts.push({
              tone: 'prime',
              badge: fmt(e.n),
              title: count === 1 ? 'Your first prime medal' : `${fmt(count)} prime medals`,
              body:
                count === 1
                  ? '2 is the only even prime. Every prime you reach adds a medal.'
                  : `${fmt(e.n)} is the ${ordinal(e.index)} prime.`,
            });
        }
        break;
      }
      case 'twin':
        raise('twin');
        // Twins get common at speed; one toast every few seconds is plenty.
        if (notify)
          toasts.push(
            {
              tone: 'twin',
              badge: '⁂',
              title: `Twin primes ${fmt(e.lo)} & ${fmt(e.hi)}`,
              body:
                e.bonus > 0 ? `Pair complete. +${fmt(e.bonus)} points` : 'Two primes, two apart.',
            },
            'twin',
          );
        break;
      case 'famous':
        raise('famous');
        for (const f of e.entries) {
          if (notify) toasts.push({ tone: 'famous', badge: fmt(f.n), title: f.name, body: f.desc });
        }
        break;
      case 'blocked':
        raise('blocked');
        if (notify)
          toasts.push(
            {
              tone: 'neutral',
              badge: e.edge === 'ceiling' ? '⇥' : '⇤',
              title: `The number line ends at ${fmt(e.limit)} for now`,
              body:
                e.edge === 'ceiling'
                  ? 'Buy the next Horizon to keep going.'
                  : 'Flip direction to head back up.',
            },
            `blocked-${e.edge}`,
          );
        break;
    }
  }

  // Auto-walk only speaks up for primes and better, at a lower volume.
  if (source === 'auto') {
    if (top === 'prime') sound.prime(true);
    else if (top === 'twin') sound.twin();
    else if (top === 'famous') sound.famous();
    return;
  }
  switch (top) {
    case 'tick':
      sound.tick();
      break;
    case 'prime':
      sound.prime();
      buzz(10);
      break;
    case 'twin':
      sound.twin();
      buzz([10, 40, 10]);
      break;
    case 'famous':
      sound.famous();
      buzz(14);
      break;
    case 'blocked':
      sound.blocked();
      buzz(30);
      break;
  }
}

/**
 * Starts sound, vibration and (unless `toasts: false`) toast notifications for game events.
 * Versions with their own way of telling the player things pass `toasts: false`.
 */
export function startFeedback(opts: { toasts?: boolean } = {}): () => void {
  notify = opts.toasts ?? true;
  return game.on(onEvents);
}
